<script lang="ts" setup>
import type { PlatformPublic } from '@base/types'
import type { ReferralReward } from '@develit-io/fubex-exchange-prisma'

const { $t } = useI18n()

useHead({ title: String($t('components.bitbeli.affiliate.title')) })

const buttonVariant = useState<'primary' | 'accent'>('buttonVariant')

const { data, error } = await useFetch('/api/platform')
let referralRewards: Partial<ReferralReward>[] = [
  {
    rewardType: 'EXCHANGE',
    rewardPercent: 0.5,
  },
  {
    rewardType: 'KYC',
    rewardEur: 5,
  },
  {
    rewardType: 'KYB',
    rewardEur: 5,
  },
]
if (error.value) {
  console.error(error.value)
  // todo: log sentry
}
else {
  const parsedData = superjsonParse<PlatformPublic>(data.value)
  referralRewards = parsedData.referralRewards
}

const exchangeData = referralRewards.find(reward => reward.rewardType === 'EXCHANGE')
const kycData = referralRewards.find(reward => reward.rewardType === 'KYC')
const kybData = referralRewards.find(reward => reward.rewardType === 'KYB')

const blueBoxes = [
  {
    id: 1,
    heading: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxOneHeading')),
    text: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxOneText')),
    icon: 'i-fubex-account-settings',
  },
  {
    id: 2,
    heading: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxTwoHeading')),
    text: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxTwoText')),
    icon: 'i-fubex-client-advice',
  },
  {
    id: 3,
    heading: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxThreeHeading')),
    text: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxThreeText')),
    icon: 'i-fubex-provision',
  },
  {
    id: 4,
    heading: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxFourHeading')),
    text: String($t('components.bitbeli.affiliate.blueBoxes.blueBoxFourText')),
    icon: 'i-fubex-crypto-buy',
  },
]
const whiteBoxes = [
  {
    heading: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxOneHeading')),
    text: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxOneText', { data: String(exchangeData ? exchangeData?.rewardPercent?.toString().replace('.', ',') : 0) })),
    icon: 'i-fubex-transaction-made',
  },
  {
    heading: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxTwoHeading')),
    text: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxTwoText', { data: kycData?.rewardEur ?? 0 })),
    icon: 'i-fubex-user-check',
  },
  {
    heading: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxThreeHeading')),
    text: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxThreeText', { data: kybData?.rewardEur ?? 0 })),
    icon: 'i-fubex-firm-check',
  },
  {
    heading: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxFourHeading')),
    text: String($t('components.bitbeli.affiliate.whiteBoxes.whiteBoxFourText')),
    icon: 'i-fubex-individual-provision',
  },
]

onMounted(() => buttonVariant.value = 'accent')
onUnmounted(() => buttonVariant.value = 'primary')
</script>

<template>
  <div>
    <!-- hero section -->
    <section bg-gradient-to-r dark:bg-gradient-to-b from="accent-200 dark:neutral-50" via-white dark="via-50% via-accent-100/50" to="primary-100 dark:neutral-100" min-h-500px relative>
      <div fubex-container pt-28px pb-96px>
        <div flex flex-col xl:flex-row items-center gap-40px>
          <div flex flex-col gap-40px my-40px>
            <h1 display:sm md:display-lg font-700 text-center xl:text-left dark:text-white v-html="$t('components.bitbeli.affiliate.heading1')" />
            <p heading6 text-center xl:text-left dark:text-white v-html="$t('components.bitbeli.affiliate.heading2')" />
          </div>
          <NuxtImg src="/img/bitcoin-img.webp" md:min-w="500px" />
        </div>
      </div>
    </section>
    <!-- /hero section -->

    <section flex justify-center>
      <div mx-4 sm:mx-6 max-w-7xl>
        <!-- Simple approach for everybody -->
        <div flex flex-col items-center>
          <h1 font-bold heading5 sm:heading2 py-12>
            {{ $t('components.bitbeli.affiliate.simpleApproach') }}
          </h1>
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <BlueBox
              v-for="(box) in blueBoxes"
              :id="box.id"
              :key="box.id"
              :heading="box.heading"
              :text="box.text"
              :icon="box.icon"
            />
          </div>
        </div>
        <!-- Simple approach for everybody -->

        <!-- Every recommendation counts -->
        <div flex flex-col items-center>
          <h1 font-bold heading5 sm:heading2 py-12>
            {{ $t('components.bitbeli.affiliate.recommendationCounts') }}
          </h1>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WhiteBox
              v-for="(box, index) in whiteBoxes"
              :key="index"
              :heading="box.heading"
              :text="box.text"
              :icon="box.icon"
            />
          </div>
        </div>
        <!-- Every recommendation counts -->
      </div>
    </section>

    <!-- Set up new account -->
    <section class="w-full flex justify-center pt-12 pb-8">
      <WidgetAuthDialogs default-view="register">
        <FButton name="hero-register" size="lg" w-full md:w-auto prefix-icon="i-fubex-user" variant="accent" class="px-10">
          {{ $t('components.bitbeli.affiliate.createAccount') }}
        </FButton>
      </WidgetAuthDialogs>
    </section>
    <!-- Set up new account -->

    <!-- contact form -->
    <section>
      <WidgetContactForm variant="accent" relative z-10>
        <h2 heading2 font-700 text-black dark:text-white v-html="$t('components.bitbeli.affiliate.contactHeading')" />
      </WidgetContactForm>
    </section>
    <!-- /contact form -->
  </div>
</template>
